import { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  ArrowLeft, Package, Users, TrendingUp, TrendingDown, AlertTriangle, 
  ShoppingCart, DollarSign, Clock, CheckCircle, XCircle, Truck, 
  BarChart3, Settings, LogOut, RefreshCw, CreditCard,
  Calendar, Bell, Search, ChevronRight, Activity, Wallet, Plug, Shield, MapPin, FileText, MessageSquare, Scale, FileSignature, UserMinus
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { hasPermission } from '../utils/permissions';

function AdminDashboardPro({ user, onLogout }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Redirect users without dashboard permissions directly to their allowed page
  useEffect(() => {
    if (user?.permissions && Array.isArray(user.permissions) && !user.permissions.includes('*')) {
      const hasDashboardAccess = user.permissions.some(p => 
        p.startsWith('orders.') || p.startsWith('clients.') || p.startsWith('products.') || p.startsWith('payments.')
      );
      if (!hasDashboardAccess && hasPermission(user.permissions, 'analytics.view')) {
        navigate(`/${currentLang}/admin/analytics`, { replace: true });
        return;
      }
    }
  }, [user, currentLang, navigate]);

  useEffect(() => {
    fetchDashboardData();
    // Auto-refresh every 5 minutes, only while the tab is visible (saves DB traffic / egress)
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') fetchDashboardData();
    }, 300000);
    const onVisible = () => { if (document.visibilityState === 'visible') fetchDashboardData(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // Background prefetch for the neighbouring admin tabs.
  // Warms the backend 60s TTL cache so that when the admin actually clicks
  // "Заказы", "Техкарты", "Клиенты" etc. — the response is already sitting in cache
  // and returns in ~150ms instead of 1-3s. Fires once, ~1.5s after the dashboard
  // is painted (so the primary render/network aren't fighting for bandwidth).
  useEffect(() => {
    if (!user) return;
    const targets = [
      '/orders?limit=500',
      '/production/food-cost',
      '/production/products-without-recipe',
      '/production/plan',
      '/admin/users',
      '/products',
    ];
    const t = setTimeout(() => {
      targets.forEach((path) => {
        // fire-and-forget; ignore errors (some paths may 403 depending on role)
        axios.get(`${API}${path}`, { headers: getAuthHeaders(), timeout: 30000 }).catch(() => {});
      });
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [user?.id]);

  const fetchDashboardData = async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    try {
      const response = await axios.get(`${API}/admin/dashboard/enhanced`, { 
        headers: getAuthHeaders() 
      });
      setStats(response.data);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      toast.error('Не удалось загрузить данные');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU').format(amount || 0) + ' ₸';
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('ru-RU').format(num || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
          <div className="text-lg text-gray-600">Загрузка панели управления...</div>
        </div>
      </div>
    );
  }

  const statusColors = {
    'Pending': 'bg-yellow-100 text-yellow-800',
    'Accepted': 'bg-blue-100 text-blue-800',
    'In Production': 'bg-purple-100 text-purple-800',
    'Ready': 'bg-green-100 text-green-800',
    'Delivered': 'bg-emerald-100 text-emerald-800',
    'Cancelled': 'bg-red-100 text-red-800'
  };

  const statusLabels = {
    'Pending': 'Ожидает',
    'Accepted': 'Принят',
    'In Production': 'В производстве',
    'Ready': 'Готов',
    'Delivered': 'Доставлен',
    'Cancelled': 'Отменён'
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-[1600px] mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to={`/${currentLang}`} className="flex items-center">
                <img 
                  src="/logo.jpg" 
                  alt="Ainanan" 
                  className="h-10 object-contain"
                />
              </Link>
              <div className="hidden md:block">
                <h1 className="text-lg font-semibold text-gray-900">Панель управления</h1>
                <p className="text-xs text-gray-500">Ainanan Control Center</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => fetchDashboardData(true)}
                disabled={refreshing}
                data-testid="refresh-dashboard-btn"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              <LanguageSwitcher />
              <Button variant="ghost" size="sm" onClick={onLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Выход
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Quick Navigation - Permission-based */}
      <div className="bg-white border-b">
        <div className="max-w-[1600px] mx-auto px-4">
          <div className="flex items-center gap-2 py-3 overflow-x-auto">
            {hasPermission(user?.permissions, 'orders.view') && (
              <Button 
                onClick={() => navigate(`/${currentLang}/admin/orders`)} 
                className="bg-emerald-700 hover:bg-emerald-800 shrink-0"
                data-testid="nav-orders-btn"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Заказы
                {stats?.urgent_orders_count > 0 && (
                  <Badge className="ml-2 bg-red-500">{stats.urgent_orders_count}</Badge>
                )}
              </Button>
            )}
            {hasPermission(user?.permissions, 'payments.view') && (
              <Button onClick={() => navigate(`/${currentLang}/admin/payments`)} variant="outline" className="shrink-0" data-testid="nav-payments-btn">
                <CreditCard className="w-4 h-4 mr-2" />
                Платежи
              </Button>
            )}
            {hasPermission(user?.permissions, ['payments.view', 'payments.edit']) && (
              <Button onClick={() => navigate(`/${currentLang}/admin/finance`)} variant="outline" className="shrink-0" data-testid="nav-finance-btn">
                <Wallet className="w-4 h-4 mr-2" />
                Финансы
              </Button>
            )}
            {hasPermission(user?.permissions, 'products.view') && (
              <Button onClick={() => navigate(`/${currentLang}/admin/products`)} variant="outline" className="shrink-0" data-testid="nav-products-btn">
                <Package className="w-4 h-4 mr-2" />
                Товары
              </Button>
            )}
            {hasPermission(user?.permissions, 'products.view') && (
              <Button onClick={() => navigate(`/${currentLang}/admin/price-list`)} variant="outline" className="shrink-0 bg-emerald-50 border-emerald-200 hover:bg-emerald-100" data-testid="nav-price-list-btn">
                <Package className="w-4 h-4 mr-2" />
                Прайс-лист
              </Button>
            )}
            {hasPermission(user?.permissions, 'clients.view') && (
              <Button onClick={() => navigate(`/${currentLang}/admin/clients`)} variant="outline" className="shrink-0" data-testid="nav-clients-btn">
                <Users className="w-4 h-4 mr-2" />
                Клиенты
              </Button>
            )}
            {hasPermission(user?.permissions, ['analytics.view', 'clients.view']) && (
              <Button onClick={() => navigate(`/${currentLang}/admin/churn`)} variant="outline" className="shrink-0" data-testid="nav-churn-btn">
                <UserMinus className="w-4 h-4 mr-2" />
                Отток
              </Button>
            )}
            {hasPermission(user?.permissions, 'support.view') && (
              <Button onClick={() => navigate(`/${currentLang}/admin/support`)} variant="outline" className="shrink-0" data-testid="nav-support-btn">
                <MessageSquare className="w-4 h-4 mr-2" />
                Поддержка
              </Button>
            )}
            {hasPermission(user?.permissions, 'delivery.view') && (
              <Button onClick={() => navigate(`/${currentLang}/admin/delivery`)} variant="outline" className="shrink-0" data-testid="nav-delivery-btn">
                <Truck className="w-4 h-4 mr-2" />
                Доставка
              </Button>
            )}
            {hasPermission(user?.permissions, 'analytics.view') && (
              <Button onClick={() => navigate(`/${currentLang}/analytics`)} variant="outline" className="shrink-0" data-testid="nav-analytics-btn">
                <BarChart3 className="w-4 h-4 mr-2" />
                Аналитика
              </Button>
            )}
            <Button onClick={() => navigate(`/${currentLang}/admin/analytics-access`)} variant="outline" className="shrink-0 bg-emerald-50 border-emerald-200 hover:bg-emerald-100" data-testid="nav-analytics-access-btn">
              <BarChart3 className="w-4 h-4 mr-2" />
              Доступ к аналитике (Telegram)
            </Button>
            <Button onClick={() => navigate(`/${currentLang}/admin/mcp-docs`)} variant="outline" className="shrink-0 bg-violet-50 border-violet-200 hover:bg-violet-100" data-testid="nav-mcp-docs-btn">
              <BarChart3 className="w-4 h-4 mr-2" />
              MCP для Claude
            </Button>
            <Button onClick={() => navigate(`/${currentLang}/admin/production`)} variant="outline" className="shrink-0 bg-amber-50 border-amber-200 hover:bg-amber-100" data-testid="nav-production-btn">
              <BarChart3 className="w-4 h-4 mr-2" />
              Склад и производство
            </Button>
            {hasPermission(user?.permissions, ['content.view', '*']) && (
              <Button onClick={() => navigate(`/${currentLang}/admin/content`)} variant="outline" className="shrink-0" data-testid="nav-content-btn">
                <FileText className="w-4 h-4 mr-2" />
                Контент
              </Button>
            )}
            {hasPermission(user?.permissions, ['settings.view', 'settings.edit']) && (
              <Button onClick={() => navigate(`/${currentLang}/admin/settings`)} variant="outline" className="shrink-0" data-testid="nav-settings-btn">
                <Settings className="w-4 h-4 mr-2" />
                Управление
              </Button>
            )}
            {hasPermission(user?.permissions, ['settings.view', 'settings.edit']) && (
              <Button onClick={() => navigate(`/${currentLang}/admin/delivery-zones`)} variant="outline" className="shrink-0 bg-orange-50 border-orange-200 hover:bg-orange-100" data-testid="nav-delivery-zones-btn">
                <Truck className="w-4 h-4 mr-2 text-orange-600" />
                Зоны доставки
              </Button>
            )}
            <Button onClick={() => navigate(`/${currentLang}/admin/containers`)} variant="outline" className="shrink-0 bg-amber-50 border-amber-200 hover:bg-amber-100" data-testid="nav-containers-btn">
              <Package className="w-4 h-4 mr-2 text-amber-600" />
              Контейнеры
            </Button>
            {hasPermission(user?.permissions, ['settings.view', 'settings.edit']) && (
              <Button onClick={() => navigate(`/${currentLang}/admin/cities`)} variant="outline" className="shrink-0 bg-teal-50 border-teal-200 hover:bg-teal-100" data-testid="nav-cities-btn">
                <MapPin className="w-4 h-4 mr-2 text-teal-600" />
                Города
              </Button>
            )}
            {hasPermission(user?.permissions, 'integrations.view') && (
              <Button onClick={() => navigate(`/${currentLang}/admin/integrations`)} variant="outline" className="shrink-0" data-testid="nav-integrations-btn">
                <Plug className="w-4 h-4 mr-2" />
                Интеграции
              </Button>
            )}
            {hasPermission(user?.permissions, 'users.view') && (
              <Button onClick={() => navigate(`/${currentLang}/admin/security`)} variant="outline" className="shrink-0" data-testid="nav-security-btn">
                <Shield className="w-4 h-4 mr-2" />
                Безопасность
              </Button>
            )}
            {hasPermission(user?.permissions, 'settings.view') && (
              <Button onClick={() => navigate(`/${currentLang}/admin/legal-pages`)} variant="outline" className="shrink-0" data-testid="nav-legal-pages-btn">
                <Scale className="w-4 h-4 mr-2" />
                Легальные страницы
              </Button>
            )}
            {hasPermission(user?.permissions, 'settings.view') && (
              <Button onClick={() => navigate(`/${currentLang}/admin/contracts`)} variant="outline" className="shrink-0 bg-sky-50 border-sky-200 hover:bg-sky-100" data-testid="nav-contracts-btn">
                <FileSignature className="w-4 h-4 mr-2" />
                Договоры B2B
              </Button>
            )}
            {hasPermission(user?.permissions, 'settings.view') && (
              <Button onClick={() => navigate(`/${currentLang}/admin/notifications`)} variant="outline" className="shrink-0" data-testid="nav-notifications-btn">
                <Bell className="w-4 h-4 mr-2" />
                Уведомления
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 py-6">
        {/* Alert Banner for Urgent Orders */}
        {stats?.urgent_orders_count > 0 && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between" data-testid="urgent-orders-alert">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-red-600 animate-pulse" />
              <div>
                <p className="font-medium text-red-800">Требуется внимание!</p>
                <p className="text-sm text-red-600">
                  {stats.urgent_orders_count} заказов ожидают обработки более 2 часов
                </p>
              </div>
            </div>
            <Button 
              onClick={() => navigate(`/${currentLang}/admin/orders?status=Pending`)}
              className="bg-red-600 hover:bg-red-700"
            >
              Обработать сейчас
            </Button>
          </div>
        )}

        {/* Main Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Today's Revenue */}
          <Card className="border-l-4 border-l-emerald-500" data-testid="today-revenue-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                Выручка сегодня
                <DollarSign className="w-4 h-4 text-emerald-600" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{formatCurrency(stats?.today_revenue)}</div>
              <div className="flex items-center mt-1">
                {stats?.revenue_change_percent >= 0 ? (
                  <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
                )}
                <span className={`text-sm ${stats?.revenue_change_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {stats?.revenue_change_percent >= 0 ? '+' : ''}{stats?.revenue_change_percent}% vs вчера
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Today's Orders */}
          <Card className="border-l-4 border-l-blue-500" data-testid="today-orders-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                Заказы сегодня
                <ShoppingCart className="w-4 h-4 text-blue-600" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{formatNumber(stats?.today_orders)}</div>
              <div className="text-sm text-gray-500 mt-1">
                Средний чек: {formatCurrency(stats?.avg_order_value)}
              </div>
            </CardContent>
          </Card>

          {/* Active Clients */}
          <Card className="border-l-4 border-l-purple-500" data-testid="clients-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                Клиенты
                <Users className="w-4 h-4 text-purple-600" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{formatNumber(stats?.total_clients)}</div>
              <div className="flex gap-3 mt-1 text-sm">
                <span className="text-gray-500">B2B: {stats?.b2b_clients}</span>
                <span className="text-gray-500">B2C: {stats?.b2c_clients}</span>
              </div>
            </CardContent>
          </Card>

          {/* Pending Payments */}
          <Card className="border-l-4 border-l-orange-500" data-testid="payments-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                Ожидает оплаты
                <Wallet className="w-4 h-4 text-orange-600" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{stats?.pending_payments || 0}</div>
              <div className="text-sm text-gray-500 mt-1">
                Собрано: {formatCurrency(stats?.total_collected)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Secondary Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-gray-500">Выручка за неделю</div>
              <div className="text-xl font-semibold">{formatCurrency(stats?.week_revenue)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-gray-500">Выручка за месяц</div>
              <div className="text-xl font-semibold">{formatCurrency(stats?.month_revenue)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-gray-500">Заказов за неделю</div>
              <div className="text-xl font-semibold">{formatNumber(stats?.week_orders)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-gray-500">Новых клиентов (месяц)</div>
              <div className="text-xl font-semibold">{formatNumber(stats?.new_clients_month)}</div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Orders by Status */}
          <Card className="lg:col-span-1" data-testid="orders-by-status">
            <CardHeader>
              <CardTitle className="text-base">Статусы заказов</CardTitle>
              <CardDescription>Распределение по статусам</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(stats?.orders_by_status || {}).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge className={statusColors[status] || 'bg-gray-100 text-gray-800'}>
                        {statusLabels[status] || status}
                      </Badge>
                    </div>
                    <span className="font-semibold">{count}</span>
                  </div>
                ))}
              </div>
              <Button 
                variant="outline" 
                className="w-full mt-4"
                onClick={() => navigate(`/${currentLang}/admin/orders`)}
              >
                Все заказы
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* Revenue Chart (Simplified) */}
          <Card className="lg:col-span-2" data-testid="revenue-chart">
            <CardHeader>
              <CardTitle className="text-base">Выручка за 7 дней</CardTitle>
              <CardDescription>Динамика продаж</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-48 flex items-end justify-between gap-2">
                {(stats?.daily_revenue || []).map((day, idx) => {
                  const maxRevenue = Math.max(...(stats?.daily_revenue || []).map(d => d.revenue)) || 1;
                  const height = (day.revenue / maxRevenue) * 100;
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center">
                      <div 
                        className="w-full bg-emerald-500 rounded-t transition-all hover:bg-emerald-600"
                        style={{ height: `${Math.max(height, 5)}%` }}
                        title={`${formatCurrency(day.revenue)}`}
                      />
                      <div className="text-xs text-gray-500 mt-2">
                        {new Date(day.date).toLocaleDateString('ru-RU', { weekday: 'short' })}
                      </div>
                      <div className="text-xs font-medium">{day.orders}</div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Top Products */}
          <Card data-testid="top-products">
            <CardHeader>
              <CardTitle className="text-base">Топ товаров (неделя)</CardTitle>
              <CardDescription>По выручке</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(stats?.top_products || []).slice(0, 5).map((product, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-medium">
                        {idx + 1}
                      </span>
                      <span className="font-medium">{product.name}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">{formatCurrency(product.revenue)}</div>
                      <div className="text-xs text-gray-500">{product.quantity} шт</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Low Stock Alerts */}
          <Card data-testid="low-stock-alerts">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                Низкий остаток
              </CardTitle>
              <CardDescription>Товары с количеством менее 20 шт</CardDescription>
            </CardHeader>
            <CardContent>
              {(stats?.low_stock_alerts || []).length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                  Все запасы в норме
                </div>
              ) : (
                <div className="space-y-3">
                  {(stats?.low_stock_alerts || []).slice(0, 5).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                      <span className="font-medium">{item.product_name}</span>
                      <Badge variant="destructive">{item.quantity} шт</Badge>
                    </div>
                  ))}
                </div>
              )}
              <Button 
                variant="outline" 
                className="w-full mt-4"
                onClick={() => navigate(`/${currentLang}/admin/production`)}
              >
                Управление запасами
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboardPro;
